import type { GridFilterModel } from "@mui/x-data-grid-pro";
import type { AnnouncementsControllerGetListParams } from "@/app2/shared/data/gen";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ANNOUNCEMENT_DELIVERY_DATETIME_FORMAT } from "../announcementAdminStatus";

dayjs.extend(customParseFormat);

/** Subset of list GET params driven by the announcements grid filter panel (mirrors gen API). */
export type AnnouncementsListGridFilterParams = Pick<
	AnnouncementsControllerGetListParams,
	| "searchTerm"
	| "authorSearch"
	| "authorSearchMode"
	| "displayStatus"
	| "displayStatusNot"
	| "deliveryDateFrom"
	| "deliveryDateTo"
	| "readPercentMin"
	| "readPercentMax"
	| "titlePresence"
	| "authorPresence"
	| "deliveryPresence"
	| "readPresence"
>;

/**
 * Parses filter values: `Date`, ISO-like strings, or strict `MM/DD/YYYY hh:mm A`.
 * Prefer strict display format before loose `Date` parsing.
 */
function parseDeliveryDateFilterDayjs(value: unknown): Dayjs | null {
	if (value == null) return null;
	if (value instanceof Date) {
		const d = dayjs(value);
		return d.isValid() ? d : null;
	}
	const s = String(value).trim();
	if (!s) return null;
	let d = dayjs(s, ANNOUNCEMENT_DELIVERY_DATETIME_FORMAT, true);
	if (d.isValid()) return d;
	d = dayjs(s);
	if (d.isValid()) return d;
	const fallback = dayjs(new Date(s));
	return fallback.isValid() ? fallback : null;
}

/**
 * Maps MUI DataGrid filter rows to list API params. Unsupported negation:
 * delivery date `not`, read `%` `!=` — those yield no matching params.
 * Status `not` maps to `displayStatusNot`.
 */
export function gridFilterModelToListParams(model: GridFilterModel): AnnouncementsListGridFilterParams {
	const out: AnnouncementsListGridFilterParams = {};

	for (const item of model.items ?? []) {
		const field = item.columnField ?? (item as {field?: string}).field;
		if (!field) continue;
		const raw = item.value;
		const op = item.operatorValue as string | undefined;

		if (field === "title" && (op === "isEmpty" || op === "isNotEmpty")) {
			out.titlePresence = op === "isEmpty" ? "empty" : "notEmpty";
			continue;
		}
		if (field === "author" && (op === "isEmpty" || op === "isNotEmpty")) {
			out.authorPresence = op === "isEmpty" ? "empty" : "notEmpty";
			continue;
		}
		if (field === "scheduledDeliveryDate") {
			if (op === "not") {
				continue;
			}
			if (op === "isEmpty" || op === "isNotEmpty") {
				out.deliveryPresence = op === "isEmpty" ? "empty" : "notEmpty";
				continue;
			}
		}
		if (field === "_read") {
			if (op === "!=") {
				continue;
			}
			if (op === "isEmpty" || op === "isNotEmpty") {
				out.readPresence = op === "isEmpty" ? "empty" : "notEmpty";
				continue;
			}
		}

		if (raw === undefined || raw === null || raw === "") {
			continue;
		}

		switch (field) {
			case "title": {
				const text = String(raw).trim();
				if (text) out.searchTerm = text;
				break;
			}
			case "scheduledDeliveryDate": {
				if (op === "not") {
					break;
				}
				const dParsed = parseDeliveryDateFilterDayjs(raw);
				if (!dParsed || !dParsed.isValid()) break;
				if (op === "after" || op === "onOrAfter") {
					out.deliveryDateFrom = dParsed.toISOString();
				} else if (op === "before" || op === "onOrBefore") {
					out.deliveryDateTo = dParsed.toISOString();
				} else if (op === "is") {
					// “is” matches the displayed minute (no seconds in UI); widen to a 1-minute window in local time.
					out.deliveryDateFrom = dParsed.startOf("minute").toISOString();
					out.deliveryDateTo = dParsed.endOf("minute").toISOString();
				} else {
					const iso = dParsed.toISOString();
					out.deliveryDateFrom = iso;
					out.deliveryDateTo = iso;
				}
				break;
			}
			case "_status": {
				const s = String(raw).trim();
				if (
					s !== "inactive" &&
					s !== "draft" &&
					s !== "scheduled" &&
					s !== "processing" &&
					s !== "live"
				) {
					break;
				}
				if (op === "not") {
					out.displayStatusNot = s;
				} else {
					out.displayStatus = s;
				}
				break;
			}
			case "author": {
				const text = String(raw).trim();
				if (!text) break;
				out.authorSearch = text;
				if (op === "startsWith") {
					out.authorSearchMode = "startsWith";
				} else if (op === "endsWith") {
					out.authorSearchMode = "endsWith";
				} else {
					out.authorSearchMode = "contains";
				}
				break;
			}
			case "_read": {
				if (op === "!=") {
					break;
				}
				const v = typeof raw === "number" ? raw : Number(raw);
				if (!Number.isFinite(v)) break;
				if (op === "=" || op === "==" || op === "equals") {
					out.readPercentMin = v;
					out.readPercentMax = v;
				} else if (op === ">=") {
					out.readPercentMin = v;
				} else if (op === "<=") {
					out.readPercentMax = v;
				} else if (op === ">") {
					out.readPercentMin = v + 0.0001;
				} else if (op === "<") {
					out.readPercentMax = v - 0.0001;
				} else {
					out.readPercentMin = v;
					out.readPercentMax = v;
				}
				break;
			}
			default:
				break;
		}
	}

	return out;
}
